import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { useState } from 'react'
import { toast } from 'sonner'
import { CalendarClock, Send, Briefcase, CheckCircle2 } from 'lucide-react'
import { Page, PageHeader, Card, EmptyState, btnSecondary } from '@/components/rm'
import { Select } from '@/components/rm/form'
import { useAssetsDue, useRemindNow, dueAt } from '@/hooks/use-client-assets'
import { useCompany } from '@/hooks/use-company'
import { useAuth } from '@/hooks/use-auth'
import { cn } from '@/lib/utils'
import { phFormat } from '@/lib/ph'

// Not /assets: that URL collides with Vite's build-output directory of the same name, and nginx
// resolves the real directory first — the page 403'd in production while working fine in dev.
export const Route = createFileRoute('/_app/service-due')({ component: ServiceDuePage })

const fmt = (d: Date) => phFormat(d, 'date', { day: 'numeric', month: 'short', year: 'numeric' })
const personName = (c: { first_name: string | null; last_name: string | null } | null) =>
  c ? [c.first_name, c.last_name].filter(Boolean).join(' ').trim() || '—' : '—'

/**
 * The quiet-week screen (spec 024 US2): every client asset coming due, oldest first, with the two
 * actions that fill a calendar — book the job, or send the reminder. Reminders exist even with
 * automation off: v1 trust is the owner pressing the button, the same sequencing review requests
 * used.
 */
function ServiceDuePage() {
  const [windowDays, setWindowDays] = useState(60)
  const due = useAssetsDue(windowDays)
  const remind = useRemindNow()
  const navigate = useNavigate()
  const { role } = useAuth()
  const rows = due.data ?? []
  const now = Date.now()

  return (
    <Page>
      <PageHeader
        eyebrow="Records"
        title="Service due"
        subtitle="Client assets coming up for a service, from the intervals you set. Completing a linked job updates the asset by itself."
        actions={role === 'company-admin' ? <ReminderSettings /> : undefined}
      />

      <div className="mb-4 mt-5 flex items-center gap-1.5">
        {[30, 60, 90, 180].map((d) => (
          <button key={d} onClick={() => setWindowDays(d)}
            className={cn('rounded-full px-3 py-1.5 text-[12.5px] font-medium',
              windowDays === d ? 'bg-brand text-brand-fg' : 'border border-token-subtle bg-token-surface text-token-secondary')}>
            {d} days
          </button>
        ))}
      </div>

      <Card>
        {rows.length === 0 && !due.isLoading ? (
          <EmptyState label={`Nothing due in the next ${windowDays} days. Assets with a service interval appear here as they come up.`} />
        ) : (
          <div className="divide-y divide-token-subtle">
            {rows.map((a) => {
              const d = dueAt(a)!
              const overdue = d.getTime() < now
              const suppressed = !a.contact?.email || a.contact.email_opt_out
              return (
                <div key={a.id} className="flex flex-wrap items-center gap-3 px-4 py-3">
                  <CalendarClock className={cn('h-4 w-4 flex-none', overdue ? 'text-token-danger' : 'text-token-muted')} />
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-[14px] font-medium text-token-primary">
                      {a.name}
                      <span className="font-normal text-token-secondary"> · {personName(a.contact)}</span>
                    </p>
                    <p className={cn('text-[12.5px]', overdue ? 'font-semibold text-token-danger' : 'text-token-secondary')}>
                      {overdue ? 'Overdue — was due ' : 'Due '}{fmt(d)}
                      {a.last_serviced_at && ` · last serviced ${fmt(new Date(a.last_serviced_at))}`}
                    </p>
                  </div>
                  {a.booked ? (
                    <span className="inline-flex items-center gap-1.5 rounded-full bg-token-success-surface px-3 py-1.5 text-[12px] font-bold text-token-success">
                      <CheckCircle2 className="h-3.5 w-3.5" /> Booked
                    </span>
                  ) : (
                    <div className="flex gap-2">
                      <button className={btnSecondary}
                        onClick={() => navigate({ to: '/jobs/new', search: { contact: a.contact?.id } })}>
                        <Briefcase className="h-4 w-4" /> Create job
                      </button>
                      <button className={btnSecondary}
                        disabled={!!suppressed || remind.isPending}
                        title={!a.contact?.email ? 'No email address on this client'
                          : a.contact?.email_opt_out ? 'This client has opted out of email' : undefined}
                        onClick={async () => {
                          try {
                            await remind.mutateAsync(a.contact!.id)
                            toast.success(`Reminder queued for ${personName(a.contact)} — one email covers everything due`)
                          } catch (e) { toast.error(e instanceof Error ? e.message : 'Could not queue the reminder') }
                        }}>
                        <Send className="h-4 w-4" /> Send reminder
                      </button>
                    </div>
                  )}
                  {suppressed && !a.booked && (
                    <span className="w-full pl-7 text-[11.5px] text-token-warning sm:w-auto sm:pl-0">
                      {!a.contact?.email ? 'No email on file' : 'Opted out of email'}
                    </span>
                  )}
                </div>
              )
            })}
          </div>
        )}
      </Card>

      <p className="mt-3 text-[12.5px] text-token-secondary">
        Add assets from a client&apos;s page. Reminders list everything due for that client in one email, never
        one email per asset, and anything with an open job booked is left alone.
      </p>
    </Page>
  )
}

/** Opt-in automation, with the same shape of guards review requests have. Admin-only. */
function ReminderSettings() {
  const { company, save } = useCompany()
  const enabled = !!company?.service_reminder_enabled
  const lead = company?.service_reminder_lead_days ?? 14

  const setEnabled = async (on: boolean) => {
    try {
      await save.mutateAsync({
        service_reminder_enabled: on,
        // Stamped at enable-time: nothing already due before this moment is ever auto-reminded.
        ...(on ? { service_reminder_enabled_at: new Date().toISOString() } : {}),
      })
      toast.success(on ? 'Automatic reminders on — only for services falling due from today' : 'Automatic reminders off')
    } catch (e) { toast.error(e instanceof Error ? e.message : 'Failed') }
  }

  return (
    <div className="flex items-center gap-3 rounded-[12px] border border-token-subtle bg-token-surface px-3.5 py-2.5">
      <label className="flex items-center gap-2 text-[13px] font-medium text-token-primary">
        <input type="checkbox" checked={enabled} onChange={(e) => setEnabled(e.target.checked)}
          className="h-4 w-4 accent-brand" />
        Remind automatically
      </label>
      {enabled && (
        <Select value={String(lead)} className="h-8 w-[130px] text-[12.5px]"
          onChange={async (e) => {
            try { await save.mutateAsync({ service_reminder_lead_days: Number(e.target.value) }); toast.success('Lead time saved') }
            catch (err) { toast.error(err instanceof Error ? err.message : 'Failed') }
          }}>
          {[7, 14, 21, 30].map((d) => <option key={d} value={d}>{d} days before</option>)}
        </Select>
      )}
    </div>
  )
}
